import "@ionic/react/css/core.css";
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";
import "./theme/variables.css";
import React, { useEffect, useRef, useState } from "react";
import { Route, Redirect, useLocation } from "react-router-dom";
import {
  IonApp,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonSpinner,
  IonTabBar,
  IonTabButton,
  IonTabs,
  setupIonicReact,
} from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { cameraOutline, listOutline, settingsOutline } from "ionicons/icons";
import Tab1 from "./pages/Mobile/Tab1";
import Tab2 from "./pages/Mobile/Tab2";
import Tab3 from "./pages/Mobile/Tab3";
import ShipmentsMobile from "./pages/Mobile/ShipmentsMobile";
import OrdersMobile from "./pages/Mobile/OrdersMobile";
import Settings from "./pages/Mobile/Settings";
import { useAuthUser } from "./context/UserManager";
import DesktopHomepage from "./pages/Desktop/DesktopHomepage/DesktopHomepage";
import DesktopHomepage2 from "./pages/Desktop/DesktopHomepage2/DesktopHomepage2";
import DesktopPrivacyPolicy from "./pages/Desktop/PrivacyPolicy/DesktopPrivacyPolicy";
import DesktopSupport from "./pages/Desktop/Support/DesktopSupport";
import DesktopTerms from "./pages/Desktop/Terms/DesktopTerms";
import { Box, Button as MuiButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { usePrimaryFetchedData } from "./context/PrimaryFetchedDataContext";
import { useUserControls } from "./context/UserControlsProvider";
import { Providers } from "./context/Providers";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import { useUserData, useUserDataMeta } from "./context/UserDataFetcher";
import DashboardBar from "./components/DashboardBar/DashboardBar";
import ManageTrucks from "./pages/Admin/ManageTrucks";
import ManageDealers from "./pages/Admin/ManageDealers";
import ManageShipments from "./pages/Admin/ManageShipments";
import DeveloperTools from "./pages/Admin/DeveloperTools";
import ScanSandbox from "./pages/Admin/ScanSandbox";
import Home from "./pages/Admin/Home";
import ViewShipments from "./pages/Admin/ViewShipments";
import ViewOrders from "./pages/Admin/ViewOrders";
import ManufacturerApiDocs from "./pages/Admin/ManufacturerApiDocs";
import ManageManufacturerReps from "./pages/Admin/ManageManufacturerReps";
import { isMobileDevice } from "./util/util";
import { isIOSNative } from "./utils/platform";
import { isNativePlatform } from "./utils/platform";
import { applyGlobalSafeAreaOffset } from "./utils/applyGlobalSafeAreaOffset";
import { getAuth, signOut } from "firebase/auth";
import { app } from "./firebaseConfig";
import { useMobileTopSpacerColor } from "./hooks/useMobileTopSpacerColor";

setupIonicReact();

const App: React.FC = () => {
  console.log("[App] Rendering App component");
  useEffect(() => {
    const applyOffset = (reason: string) => {
      applyGlobalSafeAreaOffset(40, reason);
    };

    applyOffset("app-mount");
    const retryTimer = window.setTimeout(() => applyOffset("retry-120ms"), 120);
    const handleResize = () => applyOffset("resize");
    const handleOrientation = () => applyOffset("orientationchange");
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientation);

    return () => {
      window.clearTimeout(retryTimer);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientation);
    };
  }, []);

  return (
    <Providers>
      <IonApp>
        <IonReactRouter>
          <ProtectedRoutes />
        </IonReactRouter>
      </IonApp>
    </Providers>
  );
};

const MobileProtectedRoutes: React.FC = () => {
  const { shipments } = usePrimaryFetchedData();
  const { setLoadingStatus, setUnloadLocation, possibleLocations } =
    useUserControls();
  const iosNative = isIOSNative();

  useEffect(() => {
    if (!shipments || !shipments.length) return;
    setLoadingStatus("unloading");
    // Only auto-select a location if there is exactly one option
    if (possibleLocations.length === 1) {
      setUnloadLocation(possibleLocations[0]);
    }
  }, [shipments, possibleLocations]);

  useEffect(() => {
    const updateBottomMetrics = () => {
      try {
        const tabBarEl = document.querySelector("ion-tab-bar");
        const tabsEl =
          document.querySelector("ion-tabs.ios-native-tabbar-spacer") ??
          document.querySelector("ion-tabs");
        const tabBarStyles = tabBarEl ? window.getComputedStyle(tabBarEl) : undefined;
        const spacerStyles = tabsEl
          ? window.getComputedStyle(tabsEl, "::after")
          : undefined;
        const tabBarBackground =
          tabBarStyles?.backgroundColor ||
          tabBarStyles?.getPropertyValue("background-color") ||
          null;
        const tabBarVar =
          tabBarStyles?.getPropertyValue("--ion-tab-bar-background")?.trim() ||
          null;
        const spacerBackground =
          spacerStyles?.backgroundColor ||
          spacerStyles?.getPropertyValue("background-color") ||
          null;
        const rect = tabBarEl?.getBoundingClientRect();
        if (rect) {
          const clearance = Math.max(
            window.innerHeight - rect.top + 12,
            rect.height + 12,
            96
          );
          document.documentElement.style.setProperty(
            "--mobile-bottom-toolbar-clearance",
            `${Math.round(clearance)}px`
          );
        } else {
          document.documentElement.style.removeProperty(
            "--mobile-bottom-toolbar-clearance"
          );
        }
        console.log("[MobileProtectedRoutes] bottom bar visuals", {
          tabBarFound: !!tabBarEl,
          tabBarBackground,
          tabBarVar,
          spacerFound: !!tabsEl,
          spacerBackground,
        });
      } catch (error) {
        console.warn("[MobileProtectedRoutes] failed to inspect tab bar colors", error);
      }
    };

    const timeout = window.setTimeout(updateBottomMetrics, 60);
    window.addEventListener("resize", updateBottomMetrics);
    window.addEventListener("orientationchange", updateBottomMetrics);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener("resize", updateBottomMetrics);
      window.removeEventListener("orientationchange", updateBottomMetrics);
    };
  }, [iosNative]);
  return (
    <IonTabs className={iosNative ? "ios-native-tabbar-spacer" : undefined}>
      <IonRouterOutlet defaultValue="/tab1">
        <Route exact path="/tab1" component={Tab1} />
        <Route exact path="/tab2" component={Tab2} />
        <Route exact path="/tab3" component={Tab3} />
        <Route exact path="/shipments" component={ShipmentsMobile} />
        <Route exact path="/orders" component={OrdersMobile} />
        <Route exact path="/admin" component={AdminDashboard} />
        <Route exact path="/settings" component={Settings} />
        <Route exact path="/" render={() => <Redirect to="/tab2" />} />
        <Route
          exact
          path="/homepage"
          render={() => <Redirect to="/tab1" />}
        />
      </IonRouterOutlet>
      <IonTabBar
        slot="bottom"
        className={iosNative ? "ios-native-tabbar" : undefined}
      >
        <IonTabButton tab="tab1" href="/tab1">
          <IonIcon color="primary" icon={listOutline} />
          <IonLabel>Item List</IonLabel>
        </IonTabButton>
        <IonTabButton tab="tab2" href="/tab2">
          <IonIcon color="primary" icon={cameraOutline} />
          <IonLabel>Scanner</IonLabel>
        </IonTabButton>
        <IonTabButton tab="tab3" href="/tab3">
          <IonIcon color="primary" icon={settingsOutline} />
          <IonLabel>More</IonLabel>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  );
};

const DashboardProtectedRoutes: React.FC = () => {
  return (
    <>
      <IonTabs>
        <IonRouterOutlet defaultValue="/admin/home">
          <Route exact path="/admin/home" component={Home} />
          <Route exact path="/admin/users/manage" component={AdminDashboard} />
          <Route exact path="/admin/dealers/manage" component={ManageDealers} />
          <Route exact path="/admin/trucks/manage" component={ManageTrucks} />
          <Route exact path="/admin/reps/manage" component={ManageManufacturerReps} />
          <Route exact path="/admin/shipments" component={ViewShipments} />
          <Route exact path="/admin/orders" component={ViewOrders} />
          <Route exact path="/admin/scan-sandbox" component={ScanSandbox} />
          <Route exact path="/admin/api-docs" component={ManufacturerApiDocs} />
          <Route exact path="/admin/dev-tools" component={DeveloperTools} />
          <Route
            exact
            path="/admin/shipments/manage"
            component={ManageShipments}
          />
          <Route
            exact
            path="/homepage"
            render={() => <Redirect to="/admin/home" />}
          />
          <Route
            exact
            path="/admin"
            render={() => <Redirect to="/admin/home" />}
          />
        </IonRouterOutlet>
      </IonTabs>
    </>
  );
};

const DealerProtectedRoutes: React.FC = () => {
  return (
    <>
      <IonTabs>
        <IonRouterOutlet defaultValue="/dealer/home">
          <Route exact path="/dealer/home" component={Home} />
          <Route exact path="/dealer/users/manage" component={AdminDashboard} />
          <Route exact path="/dealer/shipments" component={ViewShipments} />
          <Route exact path="/dealer/orders" component={ViewOrders} />
          <Route exact path="/dealer/scan-sandbox" component={ScanSandbox} />
          <Route
            exact
            path="/dealer"
            render={() => <Redirect to="/dealer/home" />}
          />
        </IonRouterOutlet>
      </IonTabs>
    </>
  );
};

const DriverProtectedRoutes: React.FC = () => {
  return (
    <>
      <IonTabs>
        <IonRouterOutlet defaultValue="/driver/shipments">
          <Route exact path="/driver/shipments" component={ViewShipments} />
          <Route exact path="/driver/orders" component={ViewOrders} />
          <Route
            exact
            path="/driver"
            render={() => <Redirect to="/driver/shipments" />}
          />
        </IonRouterOutlet>
      </IonTabs>
    </>
  );
};

const ProtectedRoutes: React.FC = () => {
  const { authUser, authLoading } = useAuthUser(); // from src/context/UserManager.tsx
  const { userData, userDataError } = useUserDataMeta(); // includes error info
  const location = useLocation();
  useEffect(() => {
  }, [userData, authUser])
  useEffect(() => {
    console.log("[ProtectedRoutes]", {
      authLoading,
      uid: authUser?.uid,
      hasUserData: !!userData,
      pathname: location.pathname,
    });
  }, [authLoading, authUser, userData, location.pathname]);

  if (location.pathname === "/privacy") {
    return <DesktopPrivacyPolicy />;
  }

  if (location.pathname === "/support") {
    return <DesktopSupport />;
  }

  if (location.pathname === "/terms") {
    return <DesktopTerms />;
  }

  if (location.pathname === "/homepage2") {
    return <Route path="/homepage2" component={DesktopHomepage2} />;
  }

  // Global auth loading state
  if (authLoading) {
    console.log("[ProtectedRoutes] rendering spinner because auth is loading");
    return <FullscreenLoading />;
  }

  // Special handling when visiting the public homepage (or root)
  if (location.pathname === "/homepage" || location.pathname === "/") {
    // If not signed in, show desktop homepage
    if (!authUser) {
      console.log("[ProtectedRoutes] redirecting unauthenticated user to homepage");
      return (
        <>
          <Route path="/homepage" component={DesktopHomepage} />
          {!authUser && (
            <Route exact path="/" render={() => <Redirect to="/homepage" />} />
          )}
        </>
      );
    }

    // Signed in: wait for userData to arrive before navigating
    if (!userData) {
      if (
        authUser &&
        userDataError &&
        ["permission-denied", "not-found"].includes(userDataError.code ?? "")
      ) {
        return <AccessPendingScreen />;
      }
      console.log("[ProtectedRoutes] waiting on userData for root/homepage path");
      return <FullscreenLoading />;
    }

    // userData is available — decide destination
    const isDesktopClient = !isMobileDevice() && !isNativePlatform();
    const dealerDesktop = userData.role === "dealer";
    const driverDesktop = userData.role === "driver";
    const destination = isDesktopClient
      ? driverDesktop
        ? "/driver/shipments"
        : dealerDesktop
        ? "/dealer/home"
        : "/admin/home"
      : isNativePlatform()
      ? "/tab2"
      : "/tab1";
    console.log("[ProtectedRoutes] redirecting from homepage to", destination);
    return <Redirect to={destination} />;
  }

  // Non-homepage routes: if not signed in, send to homepage
  if (!authUser) {
    console.log("[ProtectedRoutes] redirecting unauthenticated user to /homepage from non-root path", location.pathname);
    return (
      <>
        <Route path="/homepage" component={DesktopHomepage} />
        <Route exact path="/" render={() => <Redirect to="/homepage" />} />
      </>
    );
  }

  // Signed in but still waiting for userData
  if (
    authUser &&
    !authLoading &&
    !userData &&
    userDataError &&
    ["permission-denied", "not-found"].includes(userDataError.code ?? "")
  ) {
    return <AccessPendingScreen />;
  }

  if (!userData) {
    console.log("[ProtectedRoutes] rendering spinner because userData is still loading", { pathname: location.pathname });
    return <FullscreenLoading />;
  }

  // Signed in and have userData -> render appropriate protected routes
  console.log("[ProtectedRoutes] rendering protected routes", {
    isAdmin: userData.isAdmin,
    isMobile: isMobileDevice(),
  });
  const isDesktopClient = !isMobileDevice() && !isNativePlatform();
  const dealerDesktop = userData.role === "dealer";
  const driverDesktop = userData.role === "driver";
  if (isDesktopClient) {
    console.log("[ProtectedRoutes] granting desktop access", {
      isAdmin: userData.isAdmin,
      isDev: userData.isDev,
      dealerDesktop,
    });
    if (driverDesktop) {
      if (!location.pathname.startsWith("/driver")) {
        return <Redirect to="/driver/shipments" />;
      }
      return <DriverProtectedRoutes />;
    }
    return dealerDesktop ? <DealerProtectedRoutes /> : <DashboardProtectedRoutes />;
  }
  return <MobileProtectedRoutes />;
};

const FullscreenLoading: React.FC = () => {
  useMobileTopSpacerColor("#ffffff");
  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#ffffff",
      }}
    >
      <IonSpinner name="dots" />
    </Box>
  );
};

const AccessPendingScreen: React.FC = () => {
  const theme = useTheme();
  const { authUser } = useAuthUser();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const isMountedRef = useRef(true);
  const accountLabel = authUser?.email || authUser?.uid || "your account";
  const spacerColor =
    theme.palette.background?.default ||
    theme.palette.background?.paper ||
    "#f6eee2";

  useMobileTopSpacerColor(spacerColor);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleContactSupport = () => {
    if (typeof window === "undefined") {
      return;
    }
    const contactUrl = "https://www.cabinetscanner.com/#contact";
    const newWindow = window.open(contactUrl, "_blank", "noopener,noreferrer");
    if (!newWindow) {
      window.location.href = contactUrl;
    }
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut(getAuth(app));
    } catch (error) {
      console.error("[AccessPendingScreen] Failed to sign out", error);
      if (isMountedRef.current) {
        setIsSigningOut(false);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: { xs: 6, md: 8 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 520,
          textAlign: "left",
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          borderRadius: 3,
          p: { xs: 4, md: 5 },
          boxShadow: 6,
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          Access Pending
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Signed in as {accountLabel}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          This sign-in hasn&apos;t been linked to a Cabinet Scanner manufacturer account yet.
          Please ask your administrator to add {authUser?.email ? "this email" : "your account"} or
          select the email that was invited.
        </Typography>

        <Typography variant="body2" color="text.secondary">
          If you signed in with a personal Apple ID by mistake, sign out below and try again with your work account.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <MuiButton variant="contained" size="large" onClick={handleContactSupport}>
            Contact Support
          </MuiButton>
          <MuiButton
            variant="outlined"
            size="large"
            onClick={handleSignOut}
            disabled={isSigningOut}
          >
            {isSigningOut ? "Signing out…" : "Try a different account"}
          </MuiButton>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Need immediate help? Email <Box component="span" fontWeight={600}>cristopher.blunt@cabinetscanner.com</Box> and our support team will finish onboarding.
        </Typography>
      </Box>
    </Box>
  );
};

export default App;
